import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  PageHeader,
  SelectField,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  type Column,
} from '@eon/ui';
import { hooks, type GroupInvitation, type GroupMember } from '@eon/api-client';
import type { Device, Station } from '@eon/types';
import { formatDate, formatDateTime } from '@eon/utils';
import { useClientTableSort } from '@/lib/table-sort';
import { AllowanceBalance } from '@/components/AllowanceBalance';
import { AddGroupDeviceDialog } from '@/components/AddGroupDeviceDialog';
import { DeviceStatusBadge, InvitationStatusBadge } from '@/components/status/StatusBadges';
import { AddGroupMemberDialog } from '@/components/AddGroupMemberDialog';
import { CreateGroupStationDialog } from '@/components/CreateGroupStationDialog';
import { InviteGroupMemberDialog } from '@/components/InviteGroupMemberDialog';
import {
  ArrowLeft,
  Mail,
  UserMinus,
  UserPlus,
  Plus,
  Trash2,
  Unlink,
} from 'lucide-react';

const MEMBER_ROLES = ['owner', 'admin', 'manager', 'member', 'viewer'] as const;

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-muted-foreground text-sm font-medium">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

export function GroupDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupId } = useParams({ from: '/_app/groups/$groupId' });
  const { data: group, isLoading } = hooks.useAdminGroup(groupId);
  const { data: members = [], isLoading: membersLoading } = hooks.useAdminGroupMembers(groupId);
  const { data: devicesPage, isLoading: devicesLoading } = hooks.useAdminGroupDevices(groupId, {
    page: 1,
    pageSize: 100,
  });
  const { data: stationsPage, isLoading: stationsLoading } = hooks.useAdminGroupStations(groupId, {
    page: 1,
    pageSize: 100,
  });
  const { data: invitationsPage, isLoading: invitationsLoading } = hooks.useAdminGroupInvitations(
    groupId,
    { page: 1, pageSize: 50 },
  );

  const del = hooks.useDeleteGroup();
  const removeMember = hooks.useRemoveGroupMember();
  const updateRole = hooks.useUpdateGroupMemberRole();
  const unassignDevice = hooks.useUnassignGroupDevice();
  const cancelInvite = hooks.useCancelGroupInvitation();

  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createStationOpen, setCreateStationOpen] = useState(false);

  const devices = devicesPage?.data ?? [];
  const stations = stationsPage?.data ?? [];
  const invitations = invitationsPage?.data ?? [];

  const linkedDeviceIds = useMemo(() => new Set(devices.map((d) => d.id)), [devices]);
  const memberUserIds = useMemo(() => new Set(members.map((m) => m.userId)), [members]);

  const roleOptions = MEMBER_ROLES.map((r) => ({
    value: r,
    label: t(`group.role.${r}`),
  }));

  const memberColumns: Column<GroupMember>[] = [
    {
      key: 'name',
      header: t('group.name'),
      sortable: true,
      sortValue: (m) => m.name?.trim() || m.email || m.phone,
      cell: (m) => (
        <div>
          <p className="font-medium">{m.name?.trim() || m.email || m.phone || m.userId}</p>
          {m.email ? <p className="text-muted-foreground text-xs">{m.email}</p> : null}
        </div>
      ),
    },
    {
      key: 'role',
      header: t('group.role.label'),
      sortable: true,
      cell: (m) => (
        <SelectField
          value={m.role}
          onValueChange={(role) => {
            updateRole.mutate(
              { groupId, userId: m.userId, role },
              {
                onSuccess: () => toast.success(t('group.member.roleUpdated')),
                onError: (e) => toast.error(resolveApiError(e, t)),
              },
            );
          }}
          options={roleOptions}
        />
      ),
    },
    {
      key: 'allowance',
      header: t('group.member.allowance'),
      sortable: true,
      sortValue: (m) => m.allowanceKWh,
      cell: (m) => <AllowanceBalance value={m.allowanceKWh} />,
    },
    {
      key: 'joined',
      header: t('group.member.joined'),
      sortable: true,
      sortValue: (m) => m.joinedAt,
      cell: (m) => formatDate(m.joinedAt),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-16 text-right',
      cell: (m) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            const label = m.name?.trim() || m.email || m.userId;
            if (!window.confirm(label)) return;
            removeMember.mutate(
              { groupId, userId: m.userId },
              {
                onSuccess: () => toast.success(t('group.member.removed')),
                onError: (e) => toast.error(resolveApiError(e, t)),
              },
            );
          }}
        >
          <UserMinus className="text-destructive h-4 w-4" />
        </Button>
      ),
    },
  ];

  const deviceColumns: Column<Device>[] = [
    {
      key: 'name',
      header: t('device.displayName'),
      sortable: true,
      sortValue: (d) => d.displayName?.trim() || d.serialNumber,
      cell: (d) => (
        <Link to="/devices/$deviceId" params={{ deviceId: d.id }} className="font-medium hover:underline">
          {d.displayName?.trim() || d.serialNumber}
        </Link>
      ),
    },
    { key: 'serial', header: t('device.serialNumber'), sortable: true, sortValue: (d) => d.serialNumber, cell: (d) => d.serialNumber },
    { key: 'status', header: t('common.status'), sortable: true, cell: (d) => <DeviceStatusBadge status={d.status} /> },
    {
      key: 'actions',
      header: '',
      className: 'w-16 text-right',
      cell: (d) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            const label = d.displayName?.trim() || d.serialNumber;
            if (!window.confirm(label)) return;
            unassignDevice.mutate(
              { groupId, deviceId: d.id },
              {
                onSuccess: () => toast.success(t('group.device.removed')),
                onError: (e) => toast.error(resolveApiError(e, t)),
              },
            );
          }}
        >
          <Unlink className="text-destructive h-4 w-4" />
        </Button>
      ),
    },
  ];

  const stationColumns: Column<Station>[] = [
    {
      key: 'name',
      header: t('station.name'),
      sortable: true,
      cell: (s) => (
        <Link to="/stations/$stationId" params={{ stationId: s.id }} className="font-medium hover:underline">
          {s.name}
        </Link>
      ),
    },
    { key: 'address', header: t('station.address'), sortable: true, cell: (s) => s.address ?? '—' },
    {
      key: 'devices',
      header: t('nav.devices'),
      cell: (s) => s.deviceCount ?? s.devices?.length ?? 0,
    },
  ];

  const invitationColumns: Column<GroupInvitation>[] = [
    {
      key: 'contact',
      header: t('group.invite.contact'),
      sortable: true,
      sortValue: (inv) => inv.email || inv.phone,
      cell: (inv) => inv.email || inv.phone || '—',
    },
    {
      key: 'role',
      header: t('group.role.label'),
      sortable: true,
      cell: (inv) => {
        const key = `group.role.${inv.role}`;
        const label = t(key);
        return label === key ? inv.role : label;
      },
    },
    {
      key: 'status',
      header: t('group.invite.status'),
      sortable: true,
      cell: (inv) => (
        <InvitationStatusBadge status={inv.status} />
      ),
    },
    {
      key: 'expires',
      header: t('group.invite.expires'),
      sortable: true,
      sortKey: 'expires',
      sortValue: (inv) => inv.expiresAt,
      cell: (inv) => formatDateTime(inv.expiresAt),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-16 text-right',
      cell: (inv) =>
        inv.status === 'pending' ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              cancelInvite.mutate(
                { groupId, invitationId: inv.id },
                {
                  onSuccess: () => toast.success(t('group.invite.cancelled')),
                  onError: (e) => toast.error(resolveApiError(e, t)),
                },
              );
            }}
          >
            <Trash2 className="text-destructive h-4 w-4" />
          </Button>
        ) : null,
    },
  ];

  const {
    sort: memberSort,
    onSortChange: onMemberSortChange,
    sortedData: sortedMembers,
  } = useClientTableSort(members, memberColumns);
  const {
    sort: deviceSort,
    onSortChange: onDeviceSortChange,
    sortedData: sortedDevices,
  } = useClientTableSort(devices, deviceColumns);
  const {
    sort: stationSort,
    onSortChange: onStationSortChange,
    sortedData: sortedStations,
  } = useClientTableSort(stations, stationColumns);
  const {
    sort: invitationSort,
    onSortChange: onInvitationSortChange,
    sortedData: sortedInvitations,
  } = useClientTableSort(invitations, invitationColumns);

  const onDelete = () => {
    if (!group || !window.confirm(`"${group.name}"`)) return;
    del.mutate(group.id, {
      onSuccess: () => {
        toast.success(t('actions.delete'));
        navigate({ to: '/groups' });
      },
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  if (isLoading) {
    return (
      <>
        <PageHeader title={t('nav.groups')} />
        <Skeleton className="h-64 w-full" />
      </>
    );
  }

  if (!group) {
    return (
      <>
        <PageHeader title={t('group.notFound')} />
        <Button variant="outline" asChild>
          <Link to="/groups">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('group.back')}
          </Link>
        </Button>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={group.name}
        description={group.description ?? undefined}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/groups">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('group.back')}
              </Link>
            </Button>
            <Badge variant={group.active === false ? 'destructive' : 'secondary'}>
              {group.active === false ? t('group.inactive') : t('group.active')}
            </Badge>
            <Button variant="destructive" onClick={onDelete} disabled={del.isPending}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('actions.delete')}
            </Button>
          </>
        }
      />

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">{t('group.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="members">{t('group.tabs.members')}</TabsTrigger>
          <TabsTrigger value="devices">{t('group.tabs.devices')}</TabsTrigger>
          <TabsTrigger value="stations">{t('group.tabs.stations')}</TabsTrigger>
          <TabsTrigger value="invitations">{t('group.tabs.invitations')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard label={t('group.members')} value={group.memberCount} />
            <StatCard label={t('nav.devices')} value={group.deviceCount} />
            <StatCard label={t('nav.stations')} value={group.stationCount} />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>{t('group.details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{t('group.name')}</span>
                <span className="font-medium">{group.name}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{t('group.description')}</span>
                <span className="font-medium">{group.description ?? '—'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">ID</span>
                <span className="font-mono text-xs">{group.id}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{t('user.createdAt')}</span>
                <span className="font-medium">{formatDateTime(group.createdAt)}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members" className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setAddMemberOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              {t('group.member.add')}
            </Button>
            <Button variant="outline" onClick={() => setInviteOpen(true)}>
              <Mail className="mr-2 h-4 w-4" />
              {t('group.invite.send')}
            </Button>
          </div>
          <DataTable
            data={sortedMembers}
            columns={memberColumns}
            loading={membersLoading}
            rowKey={(m) => m.userId}
            emptyTitle={t('group.member.empty')}
            emptyDescription=""
            sort={memberSort}
            onSortChange={onMemberSortChange}
          />
        </TabsContent>

        <TabsContent value="devices" className="space-y-4">
          <Button onClick={() => setAddDeviceOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('group.device.add')}
          </Button>
          <DataTable
            data={sortedDevices}
            columns={deviceColumns}
            loading={devicesLoading}
            rowKey={(d) => d.id}
            emptyTitle={t('group.device.empty')}
            emptyDescription=""
            sort={deviceSort}
            onSortChange={onDeviceSortChange}
          />
        </TabsContent>

        <TabsContent value="stations" className="space-y-4">
          <Button onClick={() => setCreateStationOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('group.station.create')}
          </Button>
          <DataTable
            data={sortedStations}
            columns={stationColumns}
            loading={stationsLoading}
            rowKey={(s) => s.id}
            emptyTitle={t('group.station.empty')}
            emptyDescription=""
            sort={stationSort}
            onSortChange={onStationSortChange}
          />
        </TabsContent>

        <TabsContent value="invitations" className="space-y-4">
          <Button variant="outline" onClick={() => setInviteOpen(true)}>
            <Mail className="mr-2 h-4 w-4" />
            {t('group.invite.send')}
          </Button>
          <DataTable
            data={sortedInvitations}
            columns={invitationColumns}
            loading={invitationsLoading}
            rowKey={(inv) => inv.id}
            emptyTitle={t('group.invite.empty')}
            emptyDescription=""
            sort={invitationSort}
            onSortChange={onInvitationSortChange}
          />
        </TabsContent>
      </Tabs>

      <AddGroupDeviceDialog
        groupId={groupId}
        linkedDeviceIds={linkedDeviceIds}
        open={addDeviceOpen}
        onOpenChange={setAddDeviceOpen}
      />
      <AddGroupMemberDialog
        groupId={groupId}
        memberUserIds={memberUserIds}
        open={addMemberOpen}
        onOpenChange={setAddMemberOpen}
      />
      <InviteGroupMemberDialog groupId={groupId} open={inviteOpen} onOpenChange={setInviteOpen} />
      <CreateGroupStationDialog
        groupId={groupId}
        open={createStationOpen}
        onOpenChange={setCreateStationOpen}
      />
    </>
  );
}
